import React from 'react';
import PropTypes from 'prop-types';
import { fallbackTheme } from '../theming/fallbackTheme';
import { useTheme } from '../theming';
import { responsivePropType } from '../utils/responsiveProp';
import { StyledSvgContainer } from './Spinner.styles';
import { useMedia } from '../utils/useMedia';
import { SpinnerProps, SpinnerSize } from './types';

export const spinnerSizeMap: Record<SpinnerSize, number> = {
  xxsmall: 16,
  xsmall: 20,
  small: 20,
  medium: 32,
  large: 48,
  xlarge: 64,
  xxlarge: 78,
};

export function Spinner({ size = 'small', strokeWidth = 4, color }: SpinnerProps) {
  const { getCurrentValue } = useMedia();
  const currentSizeValue = getCurrentValue(size, 'small');
  const isSpinnerSizeValue = !(typeof currentSizeValue === 'number');
  const spinnerSize = isSpinnerSizeValue ? spinnerSizeMap[currentSizeValue] : currentSizeValue;

  const currentTheme = useTheme();
  const spinnerColor = color ? color : fallbackTheme(currentTheme).color.neutral.onSurfaceDefault;

  const radius = spinnerSize / 2 - strokeWidth;

  return (
    <StyledSvgContainer
      radius={radius}
      size={spinnerSize}
      strokeWidth={strokeWidth}
      color={spinnerColor}
    >
      <svg
        height={spinnerSize}
        width={spinnerSize}
        viewBox={`0 0 ${spinnerSize} ${spinnerSize}`}
        role="img"
        aria-label="Spinner"
      >
        <g className="first">
          <circle
            cx={spinnerSize / 2}
            cy={spinnerSize / 2}
            r={radius}
            strokeLinecap="round"
            strokeWidth={strokeWidth}
          />
        </g>
        <g className="second">
          <circle
            cx={spinnerSize / 2}
            cy={spinnerSize / 2}
            r={radius}
            strokeLinecap="round"
            strokeWidth={strokeWidth}
          />
        </g>
        <g className="reduced" fill={color}>
          <circle opacity="0.5" cx={spinnerSize / 8} cy={spinnerSize / 2} r={spinnerSize / 8} />
          <circle opacity="0.75" cx={spinnerSize / 2} cy={spinnerSize / 2} r={spinnerSize / 8} />
          <circle cx={(spinnerSize * 7) / 8} cy={spinnerSize / 2} r={spinnerSize / 8} />
        </g>
      </svg>
    </StyledSvgContainer>
  );
}

Spinner.propTypes = {
  color: PropTypes.string,
  size: responsivePropType(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  strokeWidth: PropTypes.number,
};
